import { graphHooks } from '@tunasong/graph-lib/react'
import type { Alert } from '@tunasong/models'
import type { ExportWorkflowOutput } from '@tunasong/redux'
import {
  features,
  skipToken,
  useAfterFirstScreen,
  useIsLocalHost,
  useThunkDispatch,
  workflowsApi,
} from '@tunasong/redux'
import { useEffect, useState } from 'react'
import { describeExportFailure } from '../describe-export-failure.js'
import { exportsRoute } from '../exports-route.js'

export const lastAnnouncedKey = 'tunasong.export.lastAnnounced'
const pollMs = 5000
const terminal = new Set<ExportWorkflowOutput['status']>(['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'])
/** A redriven run is back in flight: keep polling for it, but it is not a result to announce yet */
const inFlight = new Set<ExportWorkflowOutput['status']>(['RUNNING', 'PENDING_REDRIVE'])

/** Storage can throw (private mode); a failure to read means "announce nothing", never "replay" */
const readLastAnnounced = (): string | null | undefined => {
  try {
    return localStorage.getItem(lastAnnouncedKey)
  } catch {
    return undefined
  }
}
const writeLastAnnounced = (name: string) => {
  try {
    localStorage.setItem(lastAnnouncedKey, name)
  } catch {
    /* storage unavailable — the next load will treat the newest run as already seen */
  }
}

const newestTerminal = (runs: ExportWorkflowOutput[] | undefined) =>
  (runs ?? []).filter(r => terminal.has(r.status)).sort((a, b) => (a.stopDate < b.stopDate ? 1 : -1))[0]

/**
 * Announce a finished export once, wherever the user is (spec 2026-09-09 §5). Polls only while
 * a run is in flight; remembers the last announced run per browser.
 */
export const useExportReadyAlert = () => {
  /** What ui-lib's useAlert does internally; the barrel does not export it, so dispatch directly */
  const dispatch = useThunkDispatch()
  const [pollingInterval, setPollingInterval] = useState(0)
  /**
   * A local kernel mounts no export workflow, so this query answers 501 — and `base-query`
   * turns any status >= 500 into a "Failed to load data" toast. Left ungated, the hook that
   * exists to announce a finished export instead announces a failure on every local app load.
   * `hostKind` is the mechanism the kernel design provides for exactly this: a cloud-only
   * feature stands down rather than asking and logging an error (see `register-web-push` and
   * the release-notes dialog, which do the same).
   */
  const isLocalHost = useIsLocalHost()
  /** A finished export is announced, not shown on the first screen: ask once its own calls are under way (GH #2903) */
  const isAfterFirstScreen = useAfterFirstScreen()
  const { currentData: runs } = workflowsApi.useGetExportsQuery(
    isLocalHost || !isAfterFirstScreen ? skipToken : undefined,
    { pollingInterval, skipPollingIfUnfocused: true }
  )
  const anyRunning = (runs ?? []).some(r => inFlight.has(r.status))
  useEffect(() => setPollingInterval(anyRunning ? pollMs : 0), [anyRunning])

  const newest = newestTerminal(runs)
  const rootEntityId = newest?.rootEntityId
  const { entity, isSuccess, isError } = graphHooks.useEntity(rootEntityId)
  /**
   * A run is announced exactly once, so announcing while the entity query is still loading would
   * put the raw uuid in the toast for good. Wait until the name is in hand or the query has
   * settled; only a genuinely failed lookup falls back to the id.
   */
  const scopeResolved = !rootEntityId || Boolean(entity) || isSuccess || isError
  const scope = rootEntityId ? (entity?.name ?? rootEntityId) : 'all your data'

  useEffect(() => {
    if (!runs || !scopeResolved) {
      /** No response yet (RTK Query renders `currentData: undefined` first), or the scope is loading */
      return
    }
    const last = readLastAnnounced()
    if (last === undefined) {
      /** Storage threw (e.g. private mode): announce nothing, never replay */
      return
    }
    if (last === null) {
      /** First time in this browser: remember the newest terminal run, if any, without replaying it */
      writeLastAnnounced(newest?.name ?? '')
      return
    }
    if (!newest || last === newest.name) {
      return
    }
    writeLastAnnounced(newest.name)
    const announce = (value: Alert) => dispatch(features.notifications.actions.setAlert(value))
    if (newest.status === 'SUCCEEDED') {
      announce({
        severity: 'success',
        message: `Your export of ${scope} is ready.`,
        link: exportsRoute,
        actions: [{ title: 'Open exports', action: exportsRoute }],
      })
      return
    }
    announce({
      severity: 'error',
      message: `${describeExportFailure({ status: newest.status, error: newest.error, cause: newest.cause, scope })} Nothing was changed — an export only reads.`,
      link: exportsRoute,
    })
  }, [dispatch, newest, runs, scope, scopeResolved])
}
